// Marketing copy for the ShiftWave landing page. Every claim here maps to a
// feature in the ShiftWave app source (tutorial, settings and premium screens).

export interface Showcase {
    id: string;
    shot: number; // index into /images/shiftwave/ios/0N.jpg
    eyebrow: string;
    title: string;
    body: string;
    points: string[];
    premium?: boolean;
}

export const showcases: Showcase[] = [
    {
        id: 'calendar',
        shot: 1,
        eyebrow: 'Your calendar',
        title: 'Every shift at a glance',
        body: 'Your whole month, color-coded and always up to date. Tap any day to see its notes right under the calendar.',
        points: ['Day, night and off days in their own colors', 'Flip month to month or jump to any month', 'Choose which day your week starts on'],
    },
    {
        id: 'widget',
        shot: 2,
        eyebrow: 'Home screen widget',
        title: 'Your week on your home screen',
        body: 'See this week\'s shifts without opening the app. Add the ShiftWave widget from your phone\'s widget picker.',
        points: ['Seven days at a glance', 'Matches your shift colors', 'For iPhone and Android'],
    },
    {
        id: 'rotation',
        shot: 3,
        eyebrow: 'Rotations',
        title: 'Any rotation, set up once',
        body: 'Enter your pattern one time and ShiftWave repeats it forever from your start date. Or load one of 720+ ready-made templates.',
        points: ['4-on-4-off, Pitman, DuPont, 2-2-3, 5/2-5/3 and more', 'One-tap pills for On, Off, Day and Night', 'Drag to reorder, edit or remove days'],
    },
    {
        id: 'highlight',
        shot: 4,
        eyebrow: 'Color coding',
        title: 'Color-code your days',
        body: 'Pick a color for any text and every matching day lights up automatically, so your rotation reads in a second.',
        points: ['Highlight Day, Night, Off or anything you type', 'Separate with commas to color several at once', 'Great for squads, like 1/2 or A/B'],
    },
    {
        id: 'notes',
        shot: 5,
        eyebrow: 'Notes & overrides',
        title: 'Notes, overtime & swaps',
        body: 'Life doesn\'t follow the rotation. Mark court dates, overtime and traded shifts on any day without changing your pattern.',
        points: ['Add notes to any date', 'Give a single day its own color', 'Replace the day\'s text with OT, Court, Swap or anything else'],
        premium: true,
    },
    {
        id: 'quick-setup',
        shot: 6,
        eyebrow: 'Quick Setup',
        title: 'Set up in seconds',
        body: 'Enter your department\'s 4-digit code and your whole schedule loads instantly. No typing in a rotation by hand.',
        points: ['Get a code from a co-worker', 'Preview the schedule before you apply it', 'Ask us to create a code for your team'],
        premium: true,
    },
    {
        id: 'holidays',
        shot: 7,
        eyebrow: 'Holidays',
        title: 'Holidays built in',
        body: 'See the days that matter right on your calendar, and pick exactly which holidays you want to show.',
        points: ['Major holidays included', 'Select or unselect any of them', 'Marked with a celebration icon'],
    },
    {
        id: 'themes',
        shot: 8,
        eyebrow: 'Themes',
        title: 'Make it yours',
        body: 'Dark mode for the night shift, plus colorful themes for everything else.',
        points: ['Light and Dark', 'Emerald, Deep Purple and Navy'],
    },
];

export const steps = [
    { title: 'Set your start date', body: 'Pick the day your rotation begins.' },
    { title: 'Add your rotation', body: 'Type it once, load a template or enter a 4-digit code.' },
    { title: 'You\'re done', body: 'Every shift, every month, color-coded for good.' },
];

export const extras = [
    { icon: 'event', title: 'Export to your calendar', body: 'Send your schedule to Apple Calendar, Google Calendar and more.', premium: true },
    { icon: 'backup', title: 'Backup & restore', body: 'Save a backup file and move your schedule to a new phone.' },
    { icon: 'photo', title: 'Share a month', body: 'Send a picture of any month to family and friends.' },
    { icon: 'qr', title: 'Tell your co-workers', body: 'Share a download link or let them scan a QR code.' },
    { icon: 'devices', title: 'iPhone, iPad & Android', body: 'Free on the App Store and Google Play.' },
    { icon: 'lock', title: 'No account needed', body: 'Your schedule is saved on your phone. Signing in is optional.' },
];

export const audiences = ['Police', 'Fire', 'EMS', 'Nurses', 'Corrections', 'Dispatch', 'Hospitals', 'Postal', 'Manufacturing', 'Security'];

export const freeFeatures = [
    'Any repeating rotation',
    '720+ schedule templates',
    'Automatic color highlighting',
    'Holidays and themes',
    'Home screen widget',
    'Backup & restore',
];

export const premiumFeatures = [
    'Edit notes, colors and text on any day',
    'Quick Setup codes',
    'Export to Apple & Google Calendar',
    'No ads',
];

export const faqs = [
    {
        q: 'Is ShiftWave free?',
        a: 'Yes. ShiftWave is free to download and use, with ads. Premium unlocks day notes and overrides, Quick Setup codes and calendar export, and removes all ads. You can choose monthly, annual or lifetime access in the app.',
    },
    {
        q: 'Will it work with my department\'s schedule?',
        a: 'Almost certainly. ShiftWave repeats any pattern you enter, no matter how long. It also includes 720+ ready-made templates, from simple 4-on-4-off to 5/2-5/3 and 12-hour Pitman rotations.',
    },
    {
        q: 'What is a Quick Setup code?',
        a: 'A 4-digit code that loads a complete schedule in one step. Get one from a co-worker who already uses ShiftWave, or email us your rotation and we\'ll create a code your whole team can use.',
    },
    {
        q: 'Is it on Android?',
        a: 'Yes. ShiftWave is on the App Store for iPhone and iPad, and on Google Play for Android.',
    },
    {
        q: 'Can I put my shifts in Google or Apple Calendar?',
        a: 'Yes. With Premium, you can export your schedule as a calendar file that works with Apple Calendar, Google Calendar and most other calendar apps.',
    },
    {
        q: 'I use CopApp! Should I switch?',
        a: 'ShiftWave is the next generation of CopApp!, with features like a home screen widget, built-in holidays, more themes and calendar export. Both apps use the same Quick Setup codes, so moving your schedule over takes seconds.',
    },
    {
        q: 'What happens to my data?',
        a: 'Your schedule and notes are saved on your phone. You don\'t need an account to use ShiftWave. See our privacy policy for the full details.',
    },
];

// The 10 most common rotations, ranked mainly by how many of the 720 ShiftWave
// templates use each pattern, with 24/48 and 4/10s added from industry sources
// (fire departments and police shift-length surveys). Patterns are one full
// cycle: D = day shift, N = night shift, F = 24-hour shift, O = off.
export interface PopularSchedule {
    name: string;
    aka?: string;
    hours: string;
    pattern: string;
    body: string;
    usedBy: string;
}

export const popularSchedules: PopularSchedule[] = [
    {
        name: 'Pitman',
        aka: '2-2-3 · Panama',
        hours: '12-hour shifts',
        pattern: 'DDOODDDOODDOOO',
        body: 'Work 2, off 2, work 3, then flip. Every other weekend is a 3-day weekend.',
        usedBy: 'Police, corrections, manufacturing',
    },
    {
        name: '5 On / 2 Off',
        hours: '8-hour shifts',
        pattern: 'DDDDDOO',
        body: 'The classic five-day week, with the same two days off every week.',
        usedBy: 'Police, hospitals, offices',
    },
    {
        name: '4 On / 4 Off',
        hours: '12-hour shifts',
        pattern: 'DDDDOOOO',
        body: 'Four shifts on, four days off, on an easy 8-day cycle.',
        usedBy: 'Police, EMS, plants',
    },
    {
        name: '4 On / 2 Off',
        hours: '8-hour shifts',
        pattern: 'DDDDOO',
        body: 'A short 6-day cycle, so your days off move through the week.',
        usedBy: 'Police, transit',
    },
    {
        name: '5/2, 5/3',
        hours: '8-hour shifts',
        pattern: 'DDDDDOODDDDDOOO',
        body: 'Five on, two off, five on, three off: a 15-day rotation.',
        usedBy: 'Police, sheriff\'s offices',
    },
    {
        name: 'DuPont',
        hours: '12-hour shifts',
        pattern: 'NNNNOOODDDONNNOOODDDDOOOOOOO',
        body: 'Rotates between days and nights over 4 weeks, ending with 7 days off in a row.',
        usedBy: 'Plants, police, utilities',
    },
    {
        name: '24/48',
        aka: 'One on, two off',
        hours: '24-hour shifts',
        pattern: 'FOO',
        body: 'One full day on duty, then two days off.',
        usedBy: 'Fire, EMS',
    },
    {
        name: '3 On / 3 Off',
        hours: '12-hour shifts',
        pattern: 'DDDOOO',
        body: 'Three shifts on, three days off, on a 6-day cycle.',
        usedBy: 'Police, nursing, dispatch',
    },
    {
        name: '5 On / 3 Off',
        hours: '8-hour shifts',
        pattern: 'DDDDDOOO',
        body: 'Five on, three off, so your days off move a day later each week.',
        usedBy: 'Police, state police',
    },
    {
        name: '4/10s',
        aka: '4 On / 3 Off',
        hours: '10-hour shifts',
        pattern: 'DDDDOOO',
        body: 'Four 10-hour days, then three days off in a row every week.',
        usedBy: 'Police, sheriff\'s offices, public works',
    },
];
